import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from "../modes.module.css";
import AITutor from '../AITutor/AITutor';
import BackButton from '../common/BackButton/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import Loader from '../common/Loader/Loader';

const ConstructionMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [completingSession, setCompletingSession] = useState(false);
  
  // New state for drag and drop functionality
  const [wordBank, setWordBank] = useState([]);
  const [placedWords, setPlacedWords] = useState([]);
  const [blankSpots, setBlankSpots] = useState([]);
  
  // New state for question generation
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);
  const questionsGenerated = useRef(0);
  const [sessionId, setSessionId] = useState(null);
  const attemptedQuestions = useRef([]);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.construction || 1;

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startSession();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length === 1) {
      setQuestionStartTime(Date.now());
      setupDragAndDrop();
    }
  }, [currentQuestion, questions]);

  useEffect(() => {
    if (currentQuestion > 0) {
      setQuestionStartTime(Date.now());
      setupDragAndDrop();
    }
  }, [currentQuestion]);

  const startSession = async () => {
    setLoading(true);
    try {
      // Start a new session for construction mode
      const sessionData = await fetch(`${API_BASE_URL}/sessions/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mode: 'construction',
          difficulty: difficulty,
          questions: [] // Start with empty questions, we'll add them as we go
        })
      });

      if (!sessionData.ok) throw new Error('Failed to start session');
      const sessionInfo = await sessionData.json();
      
      setSessionId(sessionInfo._id);
      localStorage.setItem('currentSessionId', sessionInfo._id);
      
      // Start generating questions
      await generateNextQuestion();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const generateNextQuestion = async () => {
    setLoadingNextQuestion(true);
    try {
      // Generate the question
      const params = new URLSearchParams({
        mode: 'construction',
        difficulty: difficulty,
        type: 'rearrangement'
      });

      const response = await fetch(`${API_BASE_URL}/questions/generateOne?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch question');
      const questionData = await response.json();
      
      // Add the new question to our questions array
      setQuestions(prevQuestions => [...prevQuestions, questionData]);
      questionsGenerated.current++;
      
      // If this is the first question, stop loading
      if (questionsGenerated.current === 1) {
        setLoading(false);
      }
      
      // Continue generating questions until we have 5 questions
      if (questionsGenerated.current < 5) {
        // Generate next question in the background
        setTimeout(() => generateNextQuestion(), 500);
      }
    } catch (err) {
      console.error("Error generating question:", err);
      // Don't set error here to avoid interrupting the session
    } finally {
      setLoadingNextQuestion(false);
    }
  };

  // New function to set up drag and drop
  const setupDragAndDrop = () => {
    if (questions.length === 0) return;
    
    const currentQ = questions[currentQuestion];
    if (!currentQ) return;
    
    // Split the correct answer into words and shuffle them
    const words = currentQ.content.correctAnswer.split(/\s+/);
    const shuffledWords = [...words].sort(() => Math.random() - 0.5);
    
    setWordBank(shuffledWords.map(word => ({ id: Math.random().toString(), text: word })));
    setPlacedWords(Array(words.length).fill(null));
    setBlankSpots(Array(words.length).fill(null));
  };

  // Update the handleDrop function to properly handle replacing words
  const handleDrop = (wordId, spotIndex) => {
    const word = wordBank.find(w => w.id === wordId);
    if (!word) return;
    
    // Create a new placed words array with the word in the correct spot
    const newPlacedWords = [...placedWords];
    const existingWord = newPlacedWords[spotIndex];
    newPlacedWords[spotIndex] = word;
    setPlacedWords(newPlacedWords);
    
    // Update the word bank in a single operation - remove the dragged word and add back any existing word
    setWordBank(prev => {
      const newWordBank = prev.filter(w => w.id !== wordId);
      if (existingWord) {
        newWordBank.push(existingWord);
      }
      return newWordBank;
    });
  };

  // Handle removing a word from a blank spot back to the word bank
  const handleRemoveWord = (spotIndex) => {
    const word = placedWords[spotIndex];
    if (!word) return;
    
    // Add the word back to the word bank
    setWordBank([...wordBank, word]);
    
    // Remove the word from the placed words
    const newPlacedWords = [...placedWords];
    newPlacedWords[spotIndex] = null;
    setPlacedWords(newPlacedWords);
  };

  // Add this new function to handle dragging words back to the word bank
  const handleDragFromBlankToBank = (word, spotIndex) => {
    if (!word) return;
    
    // Add the word back to the word bank
    setWordBank([...wordBank, word]);
    
    // Remove the word from the placed words
    const newPlacedWords = [...placedWords];
    newPlacedWords[spotIndex] = null;
    setPlacedWords(newPlacedWords);
  };

  // Add this new function to handle dragging between blank spots
  const handleDragBetweenBlanks = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newPlacedWords = [...placedWords];
    const wordToMove = newPlacedWords[fromIndex];
    
    // If there's already a word in the target spot, swap them
    if (newPlacedWords[toIndex]) {
      newPlacedWords[fromIndex] = newPlacedWords[toIndex];
      newPlacedWords[toIndex] = wordToMove;
    } else {
      // Otherwise just move the word
      newPlacedWords[fromIndex] = null;
      newPlacedWords[toIndex] = wordToMove;
    }
    
    setPlacedWords(newPlacedWords);
  };

  const handleAnswer = async () => {
    if (isSubmitting) return;
    
    // Check if all spots are filled
    if (placedWords.some(word => word === null)) {
      return; // Don't submit if not all spots are filled
    }
    
    // Check if this is the last generated question and we're still generating more
    const isLastGeneratedQuestion = currentQuestion === questions.length - 1;
    const stillGeneratingQuestions = questionsGenerated.current < 5;
    
    if (isLastGeneratedQuestion && stillGeneratingQuestions && loadingNextQuestion) {
      // Don't allow submission yet - we're still generating the next question
      return;
    }
    
    setIsSubmitting(true);
    setSubmittingAnswer(true);

    try {
      const currentQ = questions[currentQuestion];
      
      // Construct the answer from the placed words
      const userAnswer = placedWords.map(word => word.text).join(' ');
      
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const submitResponse = await fetch(`${API_BASE_URL}/sessions/${sessionId}/individual-answer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question: currentQ,
          answer: userAnswer,
          timeSpent
        })
      });

      if (!submitResponse.ok) throw new Error('Failed to submit answer');
      const result = await submitResponse.json();

      setAnswers(prev => ({ ...prev, [currentQuestion]: userAnswer }));
      attemptedQuestions.current.push({...currentQ, _id: result.id});
      setShowExplanation(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
      setSubmittingAnswer(false);
    }
  };

  const handleNext = () => {
    setShowExplanation(false);
    setSelectedAnswer(null);
    
    if (currentQuestion + 1 >= 5) {
      handleCompletion();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleCompletion = async () => {
    setCompletingSession(true);
    
    try {
      const endResponse = await fetch(`${API_BASE_URL}/sessions/${sessionId}/end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!endResponse.ok) throw new Error('Failed to end session');

      const results = await endResponse.json();
      localStorage.removeItem('currentSessionId');
      updateProgress(results);
      
      // Navigate to results page with session results and questions
      navigate('/results', { 
        state: { 
          sessionResults: results,
          questions: attemptedQuestions.current
        } 
      });
    } catch (err) {
      setError(err.message);
      setCompletingSession(false);
    }
  };

  if (loading || userLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / 5) * 100;
  const isCorrect = answers[currentQuestion] === currentQ?.content.correctAnswer;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      
      <div className={styles.card}>
        <AITutor 
          currentQuestion={questions[currentQuestion]}
          userAnswer={answers[currentQuestion]}
          isCorrect={isCorrect}
        />
        <div className={styles.header}>
          <h2 className={styles.title}>Sentence Construction</h2>
          <span className={styles.questionCount}>
            Question {currentQuestion + 1} of 5
          </span>
        </div>
        
        <div className={styles.progressContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={styles.questionTextConstruction}>
          {currentQ?.content.text}
        </div>

        {/* Drag and Drop Interface */}
        {!showExplanation && (
          <>
            <div className={styles.blankSpotsContainer}>
              {placedWords.map((word, index) => (
                <div 
                  key={index} 
                  className={`${styles.blankSpot} ${word ? styles.filled : ''}`}
                  onClick={() => word && handleRemoveWord(index)}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    // Check if this is a word from the word bank
                    const wordId = e.dataTransfer.getData('wordId');
                    if (wordId) {
                      handleDrop(wordId, index);
                      return;
                    }
                    
                    // Check if this is a word from another blank spot
                    const fromIndex = e.dataTransfer.getData('placedWordIndex');
                    if (fromIndex) {
                      handleDragBetweenBlanks(parseInt(fromIndex), index);
                    }
                  }}
                >
                  {word ? (
                    <div
                      className={styles.placedWord}
                      draggable={true}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('placedWordIndex', index.toString());
                      }}
                    >
                      {word.text}
                    </div>
                  ) : ''}
                </div>
              ))}
            </div>

            <div 
              className={styles.wordBankContainer}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const placedWordIndex = e.dataTransfer.getData('placedWordIndex');
                if (placedWordIndex) {
                  const index = parseInt(placedWordIndex);
                  handleDragFromBlankToBank(placedWords[index], index);
                }
              }}
            >
              {wordBank.map(word => (
                <div 
                  key={word.id} 
                  className={styles.wordBankItem}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('wordId', word.id);
                  }}
                >
                  {word.text}
                </div>
              ))}
            </div>

            <button 
              className={`${styles.submitButton} ${placedWords.some(word => word === null) ? styles.disabled : ''}`}
              onClick={handleAnswer}
              disabled={
                isSubmitting || 
                placedWords.some(word => word === null) || 
                (currentQuestion === questions.length - 1 && 
                 questionsGenerated.current < 5 && 
                 loadingNextQuestion)
              }
            >
              {submittingAnswer ? 'Checking...' : 'Check Answer'}
            </button>
          </>
        )}

        {showExplanation && (
          <>
            <div className={`${styles.constructionResult} ${isCorrect ? styles.correct : styles.incorrect}`}>
              <div className={styles.constructedSentence}>
                {placedWords.map((word, index) => (
                  <span key={index} className={styles.wordResult}>
                    {word?.text}
                  </span>
                ))}
              </div>
              <div className={styles.correctSentenceFormat}>
                <strong>Correct sentence:</strong> {currentQ.content.correctAnswer}
              </div>
            </div>
            <div className={styles.explanation}>
              {currentQ.content.explanation}
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= 5 ? 'Finish' : 'Next Question'}
            </button>
          </>
        )}
        <BackButton />
      </div>
    </div>
  );
};

export default ConstructionMode;